import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import { GoogleMap } from "@react-google-maps/api";
import PropTypes from "prop-types";
import { useGoogleMaps } from "../../context/GoogleMapsContext";
import VehicleMarkers from "./VehicleMarkers";

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const VehicleMap = memo(({ vehicles, selectedVehicle, onSelectVehicle, theme }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 4.6097, lng: -74.0817 });
  const { isLoaded, loadError } = useGoogleMaps();
  
  // Debounce para ajuste de bounds
  const adjustBoundsTimeout = useRef(null);

  // Centrar el mapa cuando cambian los vehículos con debounce
  useEffect(() => {
    if (adjustBoundsTimeout.current) {
      clearTimeout(adjustBoundsTimeout.current);
    }

    adjustBoundsTimeout.current = setTimeout(() => {
      if (map && vehicles.length > 0 && window.google) {
        try {
          const bounds = new window.google.maps.LatLngBounds();
          let validPositions = 0;

          vehicles.forEach((vehicle) => {
            const lat = parseFloat(vehicle.Latitude || vehicle.Latitud);
            const lng = parseFloat(vehicle.Longitude || vehicle.Longitud);

            if (!isNaN(lat) && !isNaN(lng)) {
              bounds.extend({ lat, lng });
              validPositions++;
            }
          });

          if (validPositions > 0) {
            map.fitBounds(bounds);
            
            // Ajustar zoom para un solo vehículo
            if (validPositions === 1) {
              setTimeout(() => {
                if (map) map.setZoom(15);
              }, 100);
            }
          }
        } catch (error) {
          console.error("Error ajustando mapa:", error);
        }
      }
    }, 300); // Debounce de 300ms

    return () => {
      if (adjustBoundsTimeout.current) {
        clearTimeout(adjustBoundsTimeout.current);
      }
    };
  }, [map, vehicles]);

  // Optimización de selección de vehículo
  useEffect(() => {
    if (map && selectedVehicle) {
      const lat = parseFloat(selectedVehicle.Latitude || selectedVehicle.Latitud);
      const lng = parseFloat(selectedVehicle.Longitude || selectedVehicle.Longitud);

      if (!isNaN(lat) && !isNaN(lng)) {
        map.panTo({ lat, lng });
        map.setZoom(16);
      }
    }
  }, [map, selectedVehicle]);

  // Callbacks optimizados
  const onMapLoad = useCallback((mapInstance) => {
    mapRef.current = mapInstance;
    setMap(mapInstance);
  }, []);

  // Limpieza mejorada
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(mapRef.current);
        mapRef.current = null;
        setMap(null);
      }
    };
  }, []);

  // Renderizado condicional optimizado
  if (loadError) {
    return (
      <div className={`GM__${theme}-error-container`}>
        <p className={`GM__${theme}-error-message`}>
          Error al cargar Google Maps: {loadError.message}
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`GM__${theme}-loading-container`}>
        <div className={`GM__${theme}-loading-spinner-large`}></div>
      </div>
    );
  }

  return (
    <div className={`GM__${theme}-map-container`}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={12}
        onLoad={onMapLoad}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: true,
          zoomControl: true,
          styles: theme === "dark" ? [/* estilos oscuros */] : [],
          // Optimizaciones de rendimiento
          gestureHandling: "cooperative",
          clickableIcons: false,
          disableDefaultUI: false,
        }}
      >
        <VehicleMarkers
          vehicles={vehicles}
          selectedVehicle={selectedVehicle}
          onSelectVehicle={onSelectVehicle}
          theme={theme}
        />

        {(!vehicles || vehicles.length === 0) && (
          <div className={`GM__${theme}-map-overlay`}>
            <p className={`GM__${theme}-map-overlay-text`}>
              No hay vehículos para mostrar
            </p>
          </div>
        )}
      </GoogleMap>
    </div>
  );
});

VehicleMap.propTypes = {
  vehicles: PropTypes.array.isRequired,
  selectedVehicle: PropTypes.object,
  onSelectVehicle: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
};

export default VehicleMap;